import { ArrowPathIcon, ArrowRightStartOnRectangleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/hooks/useAuth';
import { useState, useEffect } from 'react';
import { useAtom } from 'jotai';
import { userAllocationsAtom } from '@/lib/store';
import Logo from '../assets/planora_logo.svg'

interface HeaderProps {
  title?: string;
  updateMessage?: string;
  isLoading?: boolean;
  onUpdate?: () => void;
}

export default function Header({
  title,
  updateMessage, 
  isLoading = false, 
  onUpdate 
}: HeaderProps) {
  const { authCredentials, logout } = useAuth();
  const [userAllocations] = useAtom(userAllocationsAtom);
  const [initials, setInitials] = useState('');
  const [userName, setUserName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');

  // Set user information from allocations data
  useEffect(() => {
    // Generate initials from email as fallback
    const generateInitialsFromEmail = () => {
      if (authCredentials?.email) {
        const emailName = authCredentials.email.split('@')[0];
        const nameParts = emailName.split(/[._-]/);
        const userInitials = nameParts.map(part => part[0]?.toUpperCase() || '').join('').slice(0, 2);
        setInitials(userInitials);

        // Create a display name from email
        setUserName(emailName.replace(/[._-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
      }
    };

    if (userAllocations && userAllocations.length > 0) {
      // Find the current user by email
      const currentUser = userAllocations.find(
        allocation => allocation.user.emailAddress === authCredentials?.email
      )?.user;
      
      if (currentUser) {
        setUserName(currentUser.displayName);
        setAvatarUrl(currentUser.avatarUrl || '');
        
        // Generate initials from display name
        const nameParts = currentUser.displayName.split(' ');
        const userInitials = nameParts.map(part => part[0]?.toUpperCase() || '').join('').slice(0, 2);
        setInitials(userInitials);
      } else {
        // Fallback to email-based initials
        generateInitialsFromEmail();
      }
    } else {
      generateInitialsFromEmail();
    }
  }, [userAllocations, authCredentials]);

  return (
    <>
      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 bg-white dark:bg-gray-800 shadow-md z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="w-64 h-auto">
                <Logo />
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {authCredentials && (
                <div className="flex items-center gap-2">
                  {avatarUrl ? (
                    <img 
                      src={avatarUrl} 
                      alt={userName} 
                      className="w-8 h-8 rounded-full"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-medium">
                      {initials}
                    </div>
                  )}
                  <div className="text-sm hidden md:block">
                    <div className="font-medium text-gray-800 dark:text-gray-200">{userName}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{authCredentials.email}</div>
                  </div>
                </div>
              )}
              
              <button
                onClick={() => {
                  console.log("Logging out");
                  logout();
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 p-2 flex items-center cursor-pointer"
                title="Sign Out"
              >
                <ArrowRightStartOnRectangleIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Content Header */}
      <div className="flex justify-between items-center mb-6 mt-40">
        {title && <h1 className="text-2xl font-bold">{title}</h1>}
        
        {onUpdate && (
          <div className="flex items-center ml-auto">
            {updateMessage && (
              <span className="text-green-600 mr-4">{updateMessage}</span>
            )}
            <button
              onClick={onUpdate}
              disabled={isLoading}
              className="px-4 py-2 bg-primary hover:opacity-90 text-white rounded-md flex items-center cursor-pointer"
            >
              <ArrowPathIcon className={`h-5 w-5 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              {isLoading ? 'Updating...' : 'Update Data'}
            </button>
          </div>
        )}
      </div>
    </>
  );
}